import { FlexBox, Icon, Tag, Text } from '@fable/components'
import { css, useTheme } from '@fable/theme'
import { Club } from '@fable/types'
import { clubRoute } from 'app/clubs/routes'
import { isPremiumClub } from 'app/clubs/utils'
import { Carousel } from 'components/carousel'
import { formatMemberCount } from 'utils'

interface Props {
  clubs: Club[]
}

export const MLPClubsCarousel = ({ clubs }: Props) => {
  const { breakpoints, colors, mediaQueries } = useTheme()
  const animation = { duration: 15000, easing: (t: number) => t }

  const getClubLink = (club: Club) => {
    const route = clubRoute({ club })
    return `${route.pathname}${route.search}`
  }

  return (
    <Carousel
      className={css`
        margin-top: 40px;
      `}
      options={{
        loop: true,
        renderMode: 'performance',
        mode: 'free',
        dragSpeed: 0.5,
        created(s) {
          s.moveToIdx(5, true, animation)
        },
        updated(s) {
          s.moveToIdx(s.track.details.abs + 5, true, animation)
        },
        animationEnded(s) {
          s.moveToIdx(s.track.details.abs + 5, true, animation)
        },
        slides: { perView: 'auto', spacing: 20 },
        breakpoints: {
          [`(min-width: ${breakpoints.mobileL}px)`]: {
            slides: { perView: 'auto', spacing: 32 },
          },
        },
      }}
      childWidths={[
        { breakpoint: 'base', width: '35%' },
        { breakpoint: breakpoints.mobileL, width: '256px' },
      ]}
    >
      {clubs.map((club, i) => (
        <a key={i} href={getClubLink(club)} target="_blank" rel="noreferrer">
          <FlexBox
            className={css`
              position: relative;
              height: 246px;
              background-color: ${colors.uiWhite};
              border-radius: 16px;
              overflow: hidden;
              flex-direction: column;
              ${mediaQueries.mobileL} {
                height: 320px;
                border-radius: 20px;
              }
            `}
          >
            {isPremiumClub(club) ? (
              <Tag
                className={css`
                  position: absolute;
                  top: 8px;
                  left: 8px;
                  border-radius: 6px;
                  ${mediaQueries.mobileL} {
                    top: 14px;
                    left: 14px;
                    border-radius: 8px;
                  }
                `}
                backgroundColor="rgba(7, 6, 7, 0.4)"
              >
                <FlexBox alignItems="center">
                  <Icon
                    type="lock"
                    className={css`
                      margin-right: 4px;
                      width: 14px;
                    `}
                    fill={colors.uiWhite}
                  />
                  <Text
                    type="label"
                    sizing="L"
                    text="Premium"
                    color={colors.uiWhite}
                  />
                </FlexBox>
              </Tag>
            ) : null}
            <img
              className={css`
                display: block;
                width: 100%;
                height: 152px;
                object-fit: cover;
                object-position: center;
                background-color: ${colors.uiPearl};
                ${mediaQueries.mobileL} {
                  height: 216px;
                }
              `}
              src={club?.creator?.pic || club?.hero_image}
              alt={club?.title}
            />
            <FlexBox
              className={css`
                padding: 12px 16px 18px;
              `}
              flexDirection="column"
              gap="4px"
            >
              <Text
                type="label"
                sizing="M"
                color={colors.uiGraphiteLight}
                truncate={1}
              >
                {club?.creator?.display_name} moderates
              </Text>
              <Text
                className={css`
                  cursor: inherit;
                `}
                tag="p"
                type="subhead"
                sizing={{ base: 'XS', mobileL: 'S' }}
                color={colors.uiGraphite}
                truncate={2}
              >
                {club?.title}
              </Text>
              <FlexBox alignItems="center" gap="2px">
                <Icon type="parent" fill={colors.uiPewterGreyLight} />
                <Text
                  type="label"
                  sizing={{ base: 'S', mobileL: 'M' }}
                  color={colors.uiPewterGreyLight}
                >
                  {formatMemberCount(club?.member_count)}
                </Text>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        </a>
      ))}
    </Carousel>
  )
}
